import React, { FC, useState, useCallback } from 'react';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import PostDetailError from 'app/modules/posts/modules/post/components/PostDetailError';
import ComposerSkeleton from 'components/posts/detail/composer/ComposerSkeleton';
import PostComposer from 'app/modules/posts/modules/post/components/composer/PostComposer';
import Composer from 'components/posts/detail/Composer';
import PostOverview from 'app/modules/posts/modules/post/components/PostOverview';
import {
  PostModalOnClose,
  PostModalQueryParams,
} from 'app/modules/posts/modules/post/hooks/usePostModal';
import PostComposerPreloader from 'app/modules/posts/modules/post/components/composer/PostComposerPreloader';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import { useTranslation } from 'react-i18next';
import FirstPostSavedModal from 'app/components/modals/firstTimeFlags/firstPostSaved/FirstPostSavedModal';

type Props = {
  queryParams: PostModalQueryParams;
  onClose: PostModalOnClose;
};

const PostLayoutVariant: FC<Props> = ({ queryParams, onClose }) => {
  const { t } = useTranslation();
  const [firstPostModal, setFirstPostModal] = useState<{
    isOpen: boolean;
    pageId?: number;
  }>({ isOpen: false });

  const handleClose: PostModalOnClose = useCallback(
    (options) => {
      if (options?.showFirstPostModal) {
        setFirstPostModal({
          isOpen: true,
          pageId: options.pageId,
        });
      }
      onClose(options);
    },
    [onClose],
  );

  const handleFirstPostModalClose = useCallback(() => {
    setFirstPostModal({ isOpen: false, pageId: undefined });
  }, []);

  if (queryParams.compose) {
    return (
      <UserPermissionGate
        scopes={[UserPermission.POST_EDIT, UserPermission.POST_CREATE]}
        exact={false}
        RenderError={() => (
          <PostDetailError
            onClose={handleClose}
            reason={t('youDontHavePermissionToCreateOrEditPosts')}
          />
        )}
      >
        <PostComposerPreloader queryParams={queryParams}>
          {({ isLoading, isEditing, initialFormState }) => {
            if (isLoading) {
              return <ComposerSkeleton onClose={handleClose} />;
            }

            if (initialFormState) {
              return (
                <PostComposer
                  isEditing={isEditing}
                  onClose={handleClose}
                  initialFormState={initialFormState}
                >
                  {({ isSaving, savePost, form, setForm }) => (
                    <Composer
                      pageType={form.page.type as PageNumberType}
                      postStatus={form.status}
                      wasPublished={form.wasPublished}
                      pageName={form.page.name}
                      onPostStatusChange={(status) => setForm({ status })}
                      onClose={() => handleClose({ postIdToOpen: form.id })}
                      isSaving={isSaving}
                      onSave={(options) => savePost(options)}
                      postId={form.id}
                    />
                  )}
                </PostComposer>
              );
            }

            return <PostDetailError onClose={handleClose} />;
          }}
        </PostComposerPreloader>
      </UserPermissionGate>
    );
  }

  if (queryParams.id) {
    return (
      <>
        <PostOverview onClose={handleClose} id={queryParams.id} />
        {firstPostModal.isOpen && firstPostModal.pageId && (
          <FirstPostSavedModal
            open={firstPostModal.isOpen}
            onClose={handleFirstPostModalClose}
            pageId={firstPostModal.pageId}
          />
        )}
      </>
    );
  }

  return <PostDetailError onClose={handleClose} />;
};

export default PostLayoutVariant;
